import React, { useCallback, useRef, useState } from "react";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import {
    Box,
    Button,
    CircularProgress,
    Stack,
    Typography,
} from "@mui/material";
import { toPng } from "html-to-image";
import { downloadPngDataUrl } from "../../admin/welcome/downloadPngDataUrl";
import { JIGSAW_MOBILE_TITLE } from "../../prototype/mobile-jigsaw/routes";
import { SLIDING_PUZZLE_QUEST_TITLE } from "../../prototype/mobile-sliding/routes";
import { WORD_WHEEL_QUEST_TITLE } from "../../prototype/mobile-word-wheel/routes";
import slidingAppIcon from "../../assets/app-icons/sliding-puzzle-quest-app-icon.png";

/**
 * YouTube channel art / cover — recommended upload 2560×1440.
 * Keep important content in the TV/desktop/mobile safe area (~1546×423 centered).
 */
export const YOUTUBE_COVER_W = 2560;
export const YOUTUBE_COVER_H = 1440;
export const YOUTUBE_COVER_FILE = "puzzle-interact-youtube-cover-2560x1440.png";

/** Centered safe area shared across YouTube channel banner crops. */
const SAFE_W = 1546;
const SAFE_H = 423;
const SAFE_LEFT = Math.round((YOUTUBE_COVER_W - SAFE_W) / 2);
const SAFE_TOP = Math.round((YOUTUBE_COVER_H - SAFE_H) / 2);

const PUBLIC = process.env.PUBLIC_URL || "";
const JIGSAW_APP_ICON = `${PUBLIC}/ad/jigsaw-muse-app-icon-512.png`;
const WORD_WHEEL_APP_ICON = `${PUBLIC}/ad/word-wheel-quest-app-icon-512.png`;
const PROMO_IMAGE = `${PUBLIC}/ad/jigsaw-muse-feature-graphic-1024x500.png`;
const SLIDING_SHOT = `${PUBLIC}/ios-sliding-snapshots/300x650bb_1.webp`;
const JIGSAW_SHOT = `${PUBLIC}/ad/jigsaw-muse-feature-graphic-1024x500.png`;

const TO_PNG_OPTS = {
    cacheBust: true,
    pixelRatio: 1,
    skipFonts: true,
};

const BRAND_STACK =
    '"Segoe UI", "Helvetica Neue", Arial, "Noto Sans", sans-serif';

/** Phone frames for the channel “apps trailer” — must fit in the 423px safe height. */
const PHONE_W = 168;
const PHONE_H = 348;
const PHONE_RADIUS = 28;
const BEZEL = 8;

const TRAILER_APPS = [
    {
        key: "sliding",
        label: "Sliding",
        title: SLIDING_PUZZLE_QUEST_TITLE,
        iconSrc: slidingAppIcon,
        shotSrc: SLIDING_SHOT,
        accent: "#0284C7",
        shotFit: "cover",
    },
    {
        key: "jigsaw",
        label: "Jigsaw",
        title: JIGSAW_MOBILE_TITLE,
        iconSrc: JIGSAW_APP_ICON,
        shotSrc: JIGSAW_SHOT,
        accent: "#7C3AED",
        shotFit: "cover",
    },
    {
        key: "word-wheel",
        label: "Word Wheel",
        title: WORD_WHEEL_QUEST_TITLE,
        iconSrc: WORD_WHEEL_APP_ICON,
        shotSrc: WORD_WHEEL_APP_ICON,
        accent: "#059669",
        shotFit: "contain",
        shotPad: true,
    },
];

function TrailerPhone({ app }) {
    const screenW = PHONE_W - BEZEL * 2;
    const screenH = PHONE_H - BEZEL * 2 - 18;
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "8px",
                width: PHONE_W + 8,
            }}
        >
            <Box
                sx={{
                    width: PHONE_W,
                    height: PHONE_H,
                    borderRadius: `${PHONE_RADIUS}px`,
                    bgcolor: "#0B1220",
                    boxShadow: `0 14px 36px rgba(0,0,0,0.45), 0 0 0 2px ${app.accent}99`,
                    position: "relative",
                    overflow: "hidden",
                    boxSizing: "border-box",
                    p: `${BEZEL}px`,
                }}
            >
                {/* Dynamic island / notch */}
                <Box
                    sx={{
                        position: "absolute",
                        top: 10,
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: 54,
                        height: 14,
                        borderRadius: 8,
                        bgcolor: "#020617",
                        zIndex: 3,
                    }}
                />
                <Box
                    sx={{
                        width: screenW,
                        height: screenH,
                        mt: "18px",
                        borderRadius: `${PHONE_RADIUS - 10}px`,
                        overflow: "hidden",
                        bgcolor: app.shotPad ? "#ECFDF5" : "#1E293B",
                        position: "relative",
                    }}
                >
                    <Box
                        component="img"
                        src={app.shotSrc}
                        alt=""
                        sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: app.shotFit,
                            objectPosition: "center",
                            display: "block",
                            p: app.shotPad ? "18%" : 0,
                            boxSizing: "border-box",
                        }}
                    />
                    <Box
                        sx={{
                            position: "absolute",
                            left: 10,
                            bottom: 10,
                            width: 36,
                            height: 36,
                            borderRadius: "10px",
                            overflow: "hidden",
                            border: "2px solid rgba(255,255,255,0.95)",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.35)",
                            bgcolor: "#FFF",
                            zIndex: 2,
                        }}
                    >
                        <Box
                            component="img"
                            src={app.iconSrc}
                            alt={app.title}
                            sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: "block",
                            }}
                        />
                    </Box>
                </Box>
            </Box>
            <Typography
                component="div"
                sx={{
                    fontFamily: BRAND_STACK,
                    fontWeight: 800,
                    fontSize: 17,
                    color: "#FFFFFF",
                    lineHeight: 1.1,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    textShadow: "0 1px 10px rgba(0,0,0,0.65)",
                    letterSpacing: "0.01em",
                }}
            >
                {app.label}
            </Typography>
        </Box>
    );
}

function YoutubeCoverCreative({ frameRef }) {
    const w = YOUTUBE_COVER_W;
    const h = YOUTUBE_COVER_H;

    return (
        <Box
            ref={frameRef}
            sx={{
                width: w,
                height: h,
                position: "relative",
                overflow: "hidden",
                boxSizing: "border-box",
                bgcolor: "#0F172A",
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    overflow: "hidden",
                    bgcolor: "#020617",
                }}
            >
                <Box
                    component="img"
                    src={PROMO_IMAGE}
                    alt=""
                    sx={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition: "center 40%",
                        filter: "saturate(0.85) brightness(0.55)",
                    }}
                />
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        background:
                            "radial-gradient(ellipse 70% 55% at 50% 50%, rgba(15,23,42,0.25) 0%, rgba(2,6,23,0.82) 100%), "
                            + "linear-gradient(90deg, rgba(2,6,23,0.88) 0%, rgba(2,6,23,0.35) 38%, rgba(2,6,23,0.2) 62%, rgba(2,6,23,0.75) 100%)",
                    }}
                />
            </Box>

            {/* Safe area — brand + apps trailer phones (visible on all crops) */}
            <Box
                sx={{
                    position: "absolute",
                    left: SAFE_LEFT,
                    top: SAFE_TOP,
                    width: SAFE_W,
                    height: SAFE_H,
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 3,
                    boxSizing: "border-box",
                    zIndex: 2,
                }}
            >
                <Box sx={{ flex: "0 1 520px", pr: 2, minWidth: 0 }}>
                    <Typography
                        component="div"
                        sx={{
                            fontFamily: BRAND_STACK,
                            fontWeight: 800,
                            fontSize: 18,
                            letterSpacing: "0.14em",
                            textTransform: "uppercase",
                            color: "#F87171",
                            mb: 1,
                            textShadow: "0 1px 10px rgba(0,0,0,0.5)",
                        }}
                    >
                        Channel trailer
                    </Typography>
                    <Typography
                        component="div"
                        sx={{
                            fontFamily: BRAND_STACK,
                            fontWeight: 800,
                            fontSize: 52,
                            letterSpacing: "-0.03em",
                            color: "#FFFFFF",
                            lineHeight: 1.08,
                            textShadow: "0 2px 18px rgba(0,0,0,0.5)",
                        }}
                    >
                        Puzzle Interact
                    </Typography>
                    <Typography
                        component="div"
                        sx={{
                            mt: 1.25,
                            fontFamily: BRAND_STACK,
                            fontWeight: 700,
                            fontSize: 26,
                            color: "rgba(255,255,255,0.94)",
                            lineHeight: 1.25,
                            textShadow: "0 1px 12px rgba(0,0,0,0.4)",
                        }}
                    >
                        See the apps — Sliding · Jigsaw · Word Wheel
                    </Typography>
                    <Typography
                        component="div"
                        sx={{
                            mt: 0.85,
                            fontFamily: BRAND_STACK,
                            fontWeight: 600,
                            fontSize: 18,
                            color: "rgba(255,255,255,0.78)",
                            lineHeight: 1.3,
                            textShadow: "0 1px 10px rgba(0,0,0,0.35)",
                        }}
                    >
                        Puzzle games for calm focus
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "flex-end",
                        gap: "22px",
                        flexShrink: 0,
                    }}
                >
                    {TRAILER_APPS.map((app) => (
                        <TrailerPhone key={app.key} app={app} />
                    ))}
                </Box>
            </Box>
        </Box>
    );
}

/**
 * Company YouTube channel cover — apps trailer: three phone mockups in the safe area.
 */
export default function CompanyYoutubeCoverPage() {
    const frameRef = useRef(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const previewMaxW = 900;
    const previewScale = previewMaxW / YOUTUBE_COVER_W;
    const previewH = Math.round(YOUTUBE_COVER_H * previewScale);

    const handleDownload = useCallback(async () => {
        const node = frameRef.current;
        if (!node || saving) return;
        setSaving(true);
        setSaveError(null);
        try {
            const dataUrl = await toPng(node, {
                ...TO_PNG_OPTS,
                width: YOUTUBE_COVER_W,
                height: YOUTUBE_COVER_H,
                backgroundColor: "#0F172A",
            });
            await downloadPngDataUrl(dataUrl, YOUTUBE_COVER_FILE);
        } catch (e) {
            setSaveError(e?.message || "Failed to download PNG");
        } finally {
            setSaving(false);
        }
    }, [saving]);

    return (
        <Box
            sx={{
                minHeight: "100vh",
                bgcolor: "#F1F5F9",
                py: { xs: 2, sm: 3 },
                px: { xs: 1.5, sm: 3 },
                boxSizing: "border-box",
            }}
        >
            <Box sx={{ maxWidth: 960, mx: "auto" }}>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
                    YouTube — Company cover
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                    Channel art as an <strong>apps trailer</strong> — three phone mockups (Sliding,
                    Jigsaw, Word Wheel) sit in YouTube&apos;s centered safe area (~{SAFE_W}×{SAFE_H})
                    so every device crop still shows the apps. Upload size {YOUTUBE_COVER_W}×
                    {YOUTUBE_COVER_H}.
                </Typography>

                <Box
                    sx={{
                        width: previewMaxW,
                        maxWidth: "100%",
                        height: previewH,
                        mx: "auto",
                        mb: 1.5,
                        position: "relative",
                        overflow: "hidden",
                        borderRadius: 1,
                        boxShadow: "0 6px 24px rgba(15,23,42,0.12)",
                        bgcolor: "#E2E8F0",
                    }}
                >
                    <Box
                        sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: YOUTUBE_COVER_W,
                            height: YOUTUBE_COVER_H,
                            transform: `scale(${previewScale})`,
                            transformOrigin: "top left",
                        }}
                    >
                        <YoutubeCoverCreative frameRef={frameRef} />
                    </Box>
                </Box>

                <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                    <Button
                        variant="contained"
                        size="medium"
                        startIcon={
                            saving ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : (
                                <SaveAltIcon />
                            )
                        }
                        disabled={saving}
                        onClick={handleDownload}
                        sx={{
                            textTransform: "none",
                            fontWeight: 700,
                            bgcolor: "#FF0000",
                            "&:hover": { bgcolor: "#CC0000" },
                        }}
                    >
                        Download PNG
                    </Button>
                </Stack>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", textAlign: "center", mt: 1 }}
                >
                    {YOUTUBE_COVER_W}×{YOUTUBE_COVER_H} · {YOUTUBE_COVER_FILE}
                </Typography>
                {saveError ? (
                    <Typography
                        variant="caption"
                        color="error"
                        sx={{ display: "block", textAlign: "center", mt: 0.5 }}
                    >
                        {saveError}
                    </Typography>
                ) : null}
            </Box>
        </Box>
    );
}
